import { COLOR, COMPANY, SHEET, FONT, SIZE, INR_COMPACT } from '../tokens';
import {
  applyPortraitPrint,
  clipSheetToColumns,
  ensureMinColumnWidths,
  estimateWrappedRowHeight,
  formatGeneratedAt,
  formatPeriod,
  setWidths,
  writeExecutiveHeader,
  writePremiumMetaBand,
  writeSectionGap,
  writeSectionTitle,
  styleTableHeader,
} from '../layout';
import { applyMobileSheetFreeze } from '../mobileCompat';
import {
  addDataTable,
  applyInrFormatToTableColumn,
  applyTableBodyFont,
  currencyColumn,
  labelTotalColumn,
  textColumn,
} from '../tableFactory';

const COLS = 17;
const WIDTHS = [6, 12, 11, 16, 16, 14, 14, 11, 10, 12, 10, 13, 13, 12, 11, 13, 22];
const DESC_COL = 17;
const CURRENCY_COLS = [12, 13, 14, 15, 16];
const DATA_ROW_HEIGHT = 24;

const HEADER_ALIGN = [
  { col: 1, align: 'center' },
  { col: 2, align: 'center' },
  { col: 3, align: 'center' },
  { col: 4, align: 'center' },
  { col: 5, align: 'center' },
  { col: 6, align: 'center' },
  { col: 7, align: 'center' },
  { col: 8, align: 'center' },
  { col: 9, align: 'center' },
  { col: 10, align: 'left' },
  { col: 11, align: 'center' },
  { col: 12, align: 'right' },
  { col: 13, align: 'right' },
  { col: 14, align: 'right' },
  { col: 15, align: 'right' },
  { col: 16, align: 'right' },
  { col: 17, align: 'left' },
];

function computeLedgerRowHeight(row) {
  const desc = row?.descriptionClean || '';
  const channel = row?.channelLabel || '';
  const client = row?.clientName || '';
  const ref = row?.receiptRef || '';
  const company = row?.clientCompany || '';
  return Math.max(
    DATA_ROW_HEIGHT,
    estimateWrappedRowHeight(desc, WIDTHS[16], { minHeight: 28, lineHeight: 16, padding: 14 }),
    estimateWrappedRowHeight(channel, WIDTHS[9], { minHeight: DATA_ROW_HEIGHT }),
    estimateWrappedRowHeight(client, WIDTHS[5], { minHeight: DATA_ROW_HEIGHT }),
    estimateWrappedRowHeight(ref, WIDTHS[4], { minHeight: DATA_ROW_HEIGHT }),
    estimateWrappedRowHeight(company, WIDTHS[6], { minHeight: DATA_ROW_HEIGHT }),
  );
}

function applyLedgerRowHeights(sheet, table, rows) {
  for (let r = table.dataStartRow; r <= table.dataEndRow; r += 1) {
    const rowIdx = r - table.dataStartRow;
    const height = computeLedgerRowHeight(rows[rowIdx]);
    const excelRow = sheet.getRow(r);
    excelRow.height = height;
    for (let c = 1; c <= COLS; c += 1) {
      if (c === DESC_COL) continue;
      const cell = sheet.getCell(r, c);
      cell.alignment = { ...cell.alignment, vertical: 'middle' };
    }
    const descCell = sheet.getCell(r, DESC_COL);
    descCell.alignment = { ...descCell.alignment, vertical: 'top', wrapText: true };
  }
}
function styleDescriptionCell(cell, { muted = false } = {}) {
  cell.alignment = {
    wrapText: true,
    vertical: 'top',
    horizontal: 'left',
    indent: 1,
    shrinkToFit: false,
  };
  cell.font = {
    name: FONT,
    size: SIZE.body,
    color: { argb: muted ? COLOR.muted : COLOR.navy },
  };
}

function styleLedgerDataRows(sheet, table, rows) {
  for (let r = table.dataStartRow; r <= table.dataEndRow; r += 1) {
    [1, 2, 3, 4, 5, 6, 7, 8, 9, 11].forEach((col) => {
      sheet.getCell(r, col).alignment = {
        horizontal: 'center',
        vertical: 'middle',
        wrapText: col >= 5 && col <= 7,
        shrinkToFit: false,
      };
    });
    sheet.getCell(r, 10).alignment = {
      horizontal: 'left',
      vertical: 'middle',
      wrapText: true,
      indent: 1,
      shrinkToFit: false,
    };
    const rowIdx = r - table.dataStartRow;
    const desc = rows[rowIdx]?.descriptionClean || '';
    sheet.getCell(r, DESC_COL).value = desc || '—';
    styleDescriptionCell(sheet.getCell(r, DESC_COL), { muted: !desc || desc === '—' });
    CURRENCY_COLS.forEach((ci) => {
      sheet.getCell(r, ci).alignment = { horizontal: 'right', vertical: 'middle', shrinkToFit: false };
    });
    if (sheet.getCell(r, 11).value === 'Captured') {
      sheet.getCell(r, 11).font = { name: FONT, size: SIZE.body, bold: true, color: { argb: COLOR.success } };
    }
  }

  if (table.totalsRow) {
    sheet.getCell(table.totalsRow, 1).alignment = { horizontal: 'center', vertical: 'middle', shrinkToFit: false };
    sheet.getCell(table.totalsRow, DESC_COL).value = '';
    sheet.getCell(table.totalsRow, DESC_COL).alignment = { vertical: 'middle', horizontal: 'left' };
    sheet.getRow(table.totalsRow).height = 28;
  }
}

export function buildTransactionsLedgerSheet(workbook, ctx, tableName, options = {}) {
  const { meta } = ctx;
  const rows = options.rows ?? ctx.rows;
  const sheetName = options.sheetName ?? SHEET.TRANSACTIONS_ALL;
  const sheetTitle = options.sheetTitle ?? 'All Transactions';

  const sheet = workbook.addWorksheet(sheetName, {
    properties: { tabColor: { argb: COLOR.navyLight }, defaultRowHeight: 20 },
  });
  setWidths(sheet, WIDTHS);
  clipSheetToColumns(sheet, COLS);

  let row = 1;
  row = writeExecutiveHeader(
    sheet,
    row,
    COMPANY.name,
    sheetTitle,
    COLS,
  );

  writePremiumMetaBand(sheet, row, COLS,
    [
      { text: `Period: ${formatPeriod(meta.from, meta.to)}`, from: 1, to: 7 },
      { text: `Generated: ${formatGeneratedAt()}`, from: 8, to: COLS },
    ],
    [
      { text: `Records: ${rows.length}`, from: 1, to: 4 },
      { text: `By: ${meta.generatedBy || COMPANY.module}`, from: 5, to: 10 },
      { text: `Report ID: ${meta.reportId || '—'}`, from: 11, to: COLS },
    ],
  );
  row += 2;
  writeSectionGap(sheet, row, COLS);
  row += 1;
  writeSectionTitle(sheet, row, 'Transaction Ledger', COLS, { center: true });
  row += 1;

  const tableRows = rows.map((r, i) => [
    i + 1,
    r.categoryLabel || 'Internal',
    r.displayDate,
    r.displayDateTime,
    r.receiptRef != null ? String(r.receiptRef) : '—',
    r.clientName,
    r.clientCompany,
    r.machineCode,
    r.typeLabel,
    r.channelLabel,
    r.statusLabel,
    r.credit || null,
    r.debit || null,
    r.amount,
    r.gst,
    r.totalInclGst,
    r.descriptionClean ? String(r.descriptionClean) : '—',
  ]);

  const table = addDataTable(sheet, {
    startRow: row,
    columns: [
      labelTotalColumn('No', { filter: false }),
      textColumn('Category', { filter: false }),
      textColumn('Transaction Date', { filter: false }),
      textColumn('Date & Time', { filter: false }),
      textColumn('Receipt / Reference', { filter: false }),
      textColumn('Client Name', { filter: false }),
      textColumn('Company', { filter: false }),
      textColumn('Machine Code', { filter: false }),
      textColumn('Transaction Type', { filter: false }),
      textColumn('Channel', { filter: false }),
      textColumn('Status', { filter: false }),
      currencyColumn('Credit (INR)', { filter: false, total: true }),
      currencyColumn('Debit (INR)', { filter: false, total: true }),
      currencyColumn('Base Amount (INR)', { filter: false, total: true }),
      currencyColumn('GST 18% (INR)', { filter: false, total: true }),
      currencyColumn('Total incl. GST (INR)', { filter: false, total: true }),
      textColumn('Description', { filter: false }),
    ],
    rows: tableRows.length ? tableRows : [['—', '—', '—', '—', '—', '—', '—', '—', '—', '—', '—', null, null, null, null, null, 'No records in period']],
    totalsRow: tableRows.length > 0,
  });

  CURRENCY_COLS.forEach((ci) => {
    applyInrFormatToTableColumn(
      sheet,
      ci,
      table.dataStartRow,
      table.totalsRow || table.dataEndRow,
      INR_COMPACT,
    );
  });
  applyTableBodyFont(sheet, table.dataStartRow, table.dataEndRow, 1, COLS);
  styleTableHeader(sheet, table.headerRow, HEADER_ALIGN);
  styleLedgerDataRows(sheet, table, rows);

  rows.forEach((r, i) => {
    const rNum = table.dataStartRow + i;
    if (r.credit) {
      const c = sheet.getCell(rNum, 12);
      c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLOR.creditBg } };
      c.font = { name: FONT, size: SIZE.body, bold: true, color: { argb: COLOR.credit } };
    }
    if (r.debit) {
      const c = sheet.getCell(rNum, 13);
      c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLOR.debitBg } };
      c.font = { name: FONT, size: SIZE.body, bold: true, color: { argb: COLOR.debit } };
    }
    if (r.typeLabel === 'Credit') {
      sheet.getCell(rNum, 9).font = { name: FONT, size: SIZE.body, bold: true, color: { argb: COLOR.credit } };
    } else if (r.typeLabel === 'Debit') {
      sheet.getCell(rNum, 9).font = { name: FONT, size: SIZE.body, bold: true, color: { argb: COLOR.debit } };
    }
  });

  applyLedgerRowHeights(sheet, table, rows);

  if (meta.mobileCompat) {
    applyMobileSheetFreeze(sheet, table.headerRow, { colSplit: 3 });
  }

  setWidths(sheet, WIDTHS);
  ensureMinColumnWidths(sheet, WIDTHS);
  clipSheetToColumns(sheet, COLS);
  applyPortraitPrint(sheet, {
    colCount: COLS,
    lastRow: table.totalsRow || table.dataEndRow,
    printTitlesRow: '1:4',
    fitOnePage: false,
    landscape: true,
  });
  ensureMinColumnWidths(sheet, WIDTHS);
  applyLedgerRowHeights(sheet, table, rows);

  return sheet;
}
